// Mirror of chave-cmb-api notification DTOs. Keep in sync.

export type TriggerType =
  | 'alarm_active'
  | 'alarm_recurrent'
  | 'box_offline'
  | 'alarm_unattended'
  | 'alarm_burst'
  | 'box_silent'
  | 'command_failed'
  | 'group_alarm'
  | 'energy_outage'
  | 'token_revoked';

export type ActionChannel = 'email' | 'whatsapp' | 'webhook';
export type RoleSelector = 'admin' | 'manager' | 'coordinator' | 'operator';

export type AlarmTypeKey =
  | 'door' | 'bolt' | 'door_lock' | 'vibration' | 'energy' | 'sound'
  | 'violation' | 'request_opening_of_safe'
  | 'maximum_time_for_open_door' | 'cable_cut_alarm';

export interface NotificationRuleTriggerDTO {
  id?: number;
  trigger_type: TriggerType;
  params: Record<string, unknown>;
}

export interface NotificationRuleActionDTO {
  id?: number;
  channel: ActionChannel;
  recipients: {
    emails?: string[];
    phones?: string[];
    roles?: RoleSelector[];
    url?: string;
  };
  template?: string | null;
  params?: {
    subject?: string;
    headers?: Record<string, string>;
    method?: 'POST' | 'PUT';
  };
}

export interface NotificationRule {
  id: number;
  name: string;
  description: string | null;
  scope: 'global' | 'company';
  company_id: number | null;
  enabled: boolean;
  created_by_user_id: number | null;
  created_at: string;
  updated_at: string;
  triggers: Array<{ id: number; trigger_type: TriggerType; params: Record<string, unknown> }>;
  actions: Array<{
    id: number;
    channel: ActionChannel;
    recipients: Record<string, unknown>;
    template: string | null;
    params: Record<string, unknown>;
  }>;
}

export interface CompanyEvolutionApi {
  id: number;
  company_id: number;
  base_url: string;
  instance_name: string;
  api_key_masked: string;
  connected: boolean;
  last_sync_at: string | null;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface RuleEvent {
  id: string;
  rule_id: number;
  trigger_type: string;
  box_id: number | null;
  company_id: number | null;
  payload: Record<string, unknown>;
  delivery_status: Record<string, unknown>;
  fired_at: string;
  created_at: string;
}

export const TRIGGER_LABELS: Record<TriggerType, string> = {
  alarm_active:                'Alarme ativo (em tempo real)',
  alarm_recurrent:             'Alarmes recorrentes (X em N dias)',
  box_offline:                 'Caixa offline há X minutos',
  alarm_unattended:            'Alarme não atendido em X minutos',
  alarm_burst:                 'Mesmo alarme N vezes em X minutos',
  box_silent:                  'Caixa silenciosa há X dias',
  command_failed:              'Comando enviado falhou',
  group_alarm:                 'Alarmes em grupo de caixas',
  energy_outage:               'Queda de energia em várias caixas',
  token_revoked:               'Token de dispositivo revogado',
};

export const CHANNEL_LABELS: Record<ActionChannel, string> = {
  email: 'Email',
  whatsapp: 'WhatsApp',
  webhook: 'Webhook',
};

export const ROLE_LABELS: Record<RoleSelector, string> = {
  admin: 'Administradores',
  manager: 'Gerentes',
  coordinator: 'Coordenadores',
  operator: 'Operadores',
};

export const ALARM_TYPE_LABELS: Record<AlarmTypeKey, string> = {
  door: 'Porta',
  bolt: 'Trava',
  door_lock: 'Fechadura',
  vibration: 'Vibração',
  energy: 'Energia',
  sound: 'Som',
  violation: 'Violação',
  request_opening_of_safe: 'Abertura do cofre',
  maximum_time_for_open_door: 'Porta aberta há muito',
  cable_cut_alarm: 'Cabo cortado',
};

export const ALARM_TYPE_VALUES: AlarmTypeKey[] = Object.keys(ALARM_TYPE_LABELS) as AlarmTypeKey[];
